import { ReactNode } from 'react'

interface ContentSectionProps {
  children: ReactNode
  className?: string
  /**
   * Variant determines the styling of the container
   * - 'default': No wrapper, just padding
   * - 'card': White card with border and shadow
   * - 'flush': No padding or wrapper
   */
  variant?: 'default' | 'card' | 'flush'
  /**
   * Padding size
   * - 'none': No padding
   * - 'sm': 1rem (4 on tailwind scale = 16px)
   * - 'md': 1.5rem (6 on tailwind scale = 24px)
   * - 'lg': 2rem (8 on tailwind scale = 32px)
   */
  padding?: 'none' | 'sm' | 'md' | 'lg'
}

const paddingClasses = {
  none: '',
  sm: 'p-4',
  md: 'p-6',
  lg: 'p-8',
}

/**
 * ContentSection Component
 *
 * Standardized wrapper for page content sections.
 * Provides consistent spacing and styling across the application.
 *
 * @example
 * // Default with medium padding
 * <ContentSection>
 *   <div>Your content</div>
 * </ContentSection>
 *
 * @example
 * // Card variant with border and shadow
 * <ContentSection variant="card" padding="md">
 *   <Tabs items={tabItems} />
 * </ContentSection>
 *
 * @example
 * // Flush (no padding/wrapper) - useful for full-width content
 * <ContentSection variant="flush">
 *   <Table />
 * </ContentSection>
 */
export const ContentSection = ({
  children,
  className = '',
  variant = 'default',
  padding = 'md',
}: ContentSectionProps) => {
  const paddingClass = paddingClasses[padding]

  if (variant === 'flush') {
    return <>{children}</>
  }

  if (variant === 'card') {
    return (
      <section className={paddingClass}>
        <div className={`rounded border border-gray-100 bg-white shadow-sm ${className}`}>
          {children}
        </div>
      </section>
    )
  }

  // default variant
  return <section className={`${paddingClass} ${className}`}>{children}</section>
}

export default ContentSection
